import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Mail, MessageSquare, TrendingUp, RefreshCw, Eye } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { API, authHeaders, apiErr } from './adminApi';
import { ContactsTable, QuotesTable } from './InboxTables';
import TrafficSection from './TrafficSection';
import ConversationDialog from './ConversationDialog';
import { StatCard, CategoryList, InboxChart } from './inbox/InboxKpis';
import BulkActionBar from './inbox/BulkActionBar';
import DashboardOverview from './DashboardOverview';

const AdminInbox = ({ onUnauthorized }) => {
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unrepliedOnly, setUnrepliedOnly] = useState(false);
  const [threadEmail, setThreadEmail] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectedQuotes, setSelectedQuotes] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, q] = await Promise.all([
        axios.get(`${API}/api/admin/summary`, { headers: authHeaders() }),
        axios.get(`${API}/api/contact`, { headers: authHeaders() }),
        axios.get(`${API}/api/quote`, { headers: authHeaders() }),
      ]);
      setSummary(s.data);
      setContacts(
        [...c.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
      setQuotes(
        [...q.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error(`Failed to load: ${apiErr(err)}`);
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]); // API/apiErr/authHeaders are module-scoped imports and stable

  useEffect(() => { load(); }, [load]);

  // Optimistic status patch
  const patchStatus = async (kind, id, status) => {
    const setter = kind === 'contact' ? setContacts : setQuotes;
    setter((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    setSummary((prev) => {
      if (!prev) return prev;
      const key = kind === 'contact' ? 'contacts_unreplied' : 'quotes_unreplied';
      const delta = status === 'replied' ? -1 : 1;
      return { ...prev, totals: { ...prev.totals, [key]: Math.max(0, (prev.totals[key] || 0) + delta) } };
    });
    try {
      const url = `${API}/api/${kind}/${id}`;
      await axios.patch(url, { status }, { headers: authHeaders() });
    } catch (err) {
      toast.error(`Status update failed: ${apiErr(err)}`);
      load(); // rehydrate from server
    }
  };

  // Delete a single contact or quote (with confirmation)
  const deleteItem = async (kind, row) => {
    const label = kind === 'contact' ? 'contact message' : 'quote request';
    const ok = window.confirm(
      `Permanently delete this ${label} from ${row.name || row.email}?\n\nThis cannot be undone.`
    );
    if (!ok) return;

    // Optimistic remove
    const setter = kind === 'contact' ? setContacts : setQuotes;
    const backup = kind === 'contact' ? contacts : quotes;
    setter((prev) => prev.filter((r) => r.id !== row.id));

    try {
      await axios.delete(`${API}/api/${kind}/${row.id}`, { headers: authHeaders() });
      toast.success(`Deleted ${label}`);
      load(); // refresh KPIs & summary
    } catch (err) {
      // Roll back
      setter(backup);
      toast.error(`Delete failed: ${apiErr(err)}`);
    }
  };

  // ---- Multi-select helpers ----
  const setterFor = (kind) => (kind === 'contact' ? setSelectedContacts : setSelectedQuotes);
  const selectedFor = (kind) => (kind === 'contact' ? selectedContacts : selectedQuotes);

  const toggleOne = (kind, id, checked) => {
    setterFor(kind)((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (kind, rows, checked) => {
    setterFor(kind)(() => (checked ? new Set(rows.map((r) => r.id)) : new Set()));
  };

  // Bulk delete selected items in a single request
  const bulkDelete = async (kind) => {
    const ids = Array.from(selectedFor(kind));
    if (ids.length === 0) return;
    const label = kind === 'contact' ? 'contact message' : 'quote request';
    const labelPlural = ids.length === 1 ? label : `${label}s`;
    const ok = window.confirm(
      `Permanently delete ${ids.length} ${labelPlural}?\n\nThis cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    const idSet = new Set(ids);
    const setter = kind === 'contact' ? setContacts : setQuotes;
    const backup = kind === 'contact' ? contacts : quotes;
    // Optimistic remove
    setter((prev) => prev.filter((r) => !idSet.has(r.id)));

    try {
      const { data } = await axios.post(
        `${API}/api/${kind}/bulk-delete`,
        { ids },
        { headers: authHeaders() },
      );
      toast.success(`Deleted ${data.deleted ?? ids.length} ${labelPlural}`);
      setterFor(kind)(new Set());
      load();
    } catch (err) {
      setter(backup);
      toast.error(`Bulk delete failed: ${apiErr(err)}`);
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading || !summary) {
    return <p className="text-gray-400" data-testid="admin-inbox-loading">Loading…</p>;
  }

  const { totals, timeline, contacts_by_category, quotes_by_service } = summary;
  const contactsUnreplied = totals.contacts_unreplied ?? contacts.filter((c) => c.status !== 'replied').length;
  const quotesUnreplied = totals.quotes_unreplied ?? quotes.filter((q) => q.status !== 'replied').length;

  const visibleContacts = unrepliedOnly ? contacts.filter((c) => c.status !== 'replied') : contacts;
  const visibleQuotes = unrepliedOnly ? quotes.filter((q) => q.status !== 'replied') : quotes;

  // Count total interactions per email (contacts + quotes combined) for thread badges
  const threadCounts = {};
  for (const c of contacts) {
    const e = c.email?.toLowerCase();
    if (e) threadCounts[e] = (threadCounts[e] || 0) + 1;
  }
  for (const q of quotes) {
    const e = q.email?.toLowerCase();
    if (e) threadCounts[e] = (threadCounts[e] || 0) + 1;
  }

  return (
    <section data-testid="admin-inbox-tab" className="space-y-6">
      <DashboardOverview activeAnnouncement={summary?.banner_active} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Inbox &amp; analytics</h2>
          <p className="text-sm text-gray-400">Last 30 days of contact form and quote submissions</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2" data-testid="unreplied-filter-wrap">
            <Switch
              id="unreplied-filter"
              data-testid="unreplied-filter"
              checked={unrepliedOnly}
              onCheckedChange={setUnrepliedOnly}
            />
            <Label htmlFor="unreplied-filter" className="text-gray-300 text-sm cursor-pointer">
              Show only unreplied
            </Label>
          </div>
          <Button data-testid="inbox-refresh" onClick={load} variant="outline" size="sm" className="border-slate-700 text-white hover:bg-slate-800">
            <RefreshCw className="w-3 h-3 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          icon={Mail}
          label="Contacts"
          value={totals.contacts}
          sub={`${contactsUnreplied} unreplied`}
        />
        <StatCard
          icon={MessageSquare}
          label="Quote Requests"
          value={totals.quotes}
          sub={`${quotesUnreplied} unreplied`}
        />
        <StatCard
          icon={Eye}
          label="Visitors"
          value={totals.unique_visitors ?? 0}
          sub={`${totals.pageviews ?? 0} page views`}
        />
        <StatCard icon={TrendingUp} label="Top Contact Cat." value={Object.keys(contacts_by_category)[0] || '—'} sub={Object.values(contacts_by_category)[0] ? `${Object.values(contacts_by_category)[0]} submissions` : ''} />
        <StatCard icon={TrendingUp} label="Top Quote Service" value={Object.keys(quotes_by_service)[0] || '—'} sub={Object.values(quotes_by_service)[0] ? `${Object.values(quotes_by_service)[0]} requests` : ''} />
      </div>

      <InboxChart timeline={timeline} />

      <TrafficSection summary={summary} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CategoryList title="Contacts by category" data={contacts_by_category} />
        <CategoryList title="Quotes by service" data={quotes_by_service} />
      </div>

      <Tabs defaultValue="contacts">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger data-testid="inbox-subtab-contacts" value="contacts" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            Contacts ({visibleContacts.length}{unrepliedOnly && contacts.length !== visibleContacts.length ? ` / ${contacts.length}` : ''})
          </TabsTrigger>
          <TabsTrigger data-testid="inbox-subtab-quotes" value="quotes" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
            Quotes ({visibleQuotes.length}{unrepliedOnly && quotes.length !== visibleQuotes.length ? ` / ${quotes.length}` : ''})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="contacts" className="mt-4 space-y-3">
          <BulkActionBar
            count={selectedContacts.size}
            onClear={() => setSelectedContacts(new Set())}
            onDelete={() => bulkDelete('contact')}
            deleting={bulkDeleting}
            testidPrefix="contacts"
          />
          <ContactsTable
            rows={visibleContacts}
            onMarkReplied={(r) => patchStatus('contact', r.id, 'replied')}
            onUnmark={(r) => patchStatus('contact', r.id, 'new')}
            onDelete={(r) => deleteItem('contact', r)}
            threadCounts={threadCounts}
            onOpenThread={setThreadEmail}
            selectedIds={selectedContacts}
            onToggleOne={(id, checked) => toggleOne('contact', id, checked)}
            onToggleAll={(checked) => toggleAll('contact', visibleContacts, checked)}
          />
        </TabsContent>
        <TabsContent value="quotes" className="mt-4 space-y-3">
          <BulkActionBar
            count={selectedQuotes.size}
            onClear={() => setSelectedQuotes(new Set())}
            onDelete={() => bulkDelete('quote')}
            deleting={bulkDeleting}
            testidPrefix="quotes"
          />
          <QuotesTable
            rows={visibleQuotes}
            onMarkReplied={(r) => patchStatus('quote', r.id, 'replied')}
            onUnmark={(r) => patchStatus('quote', r.id, 'pending')}
            onDelete={(r) => deleteItem('quote', r)}
            threadCounts={threadCounts}
            onOpenThread={setThreadEmail}
            selectedIds={selectedQuotes}
            onToggleOne={(id, checked) => toggleOne('quote', id, checked)}
            onToggleAll={(checked) => toggleAll('quote', visibleQuotes, checked)}
          />
        </TabsContent>
      </Tabs>

      <ConversationDialog
        open={Boolean(threadEmail)}
        onOpenChange={(v) => !v && setThreadEmail(null)}
        email={threadEmail}
        onThreadDeleted={() => { setThreadEmail(null); load(); }}
      />
    </section>
  );
};

export default AdminInbox;
